import {useCallback, useRef, useState} from 'react';
import {priority} from '@priority-software/priority-proto-api/lib';
import {fetchAdditionalChooses, getSearchTypeSystemConst} from '../../../../../actions';
import {promiseDebounce} from '../../../../../../../_legacy/library/utils/debounce';
import {defaultSearchFilter} from '@priority-software/priority-style-react';

export type SearchState<T> = {
    // API props
    hasMore: boolean;
    results: Array<T> | null;
    searchType?: priority.netitems.shared.FieldSelectType;
    // requestProps
    IgnoreCase: boolean;
    FromStart: boolean;
    Pattern: string;
    Page?: number;
    Preferred?: priority.netitems.shared.FieldSelectType;

    // hook props

    isSearching: boolean;
    isLoading: boolean;
    isDesc?: boolean;
    isScrolling: boolean;
    isNoResults: boolean;
    hasClientSearch: boolean;
    showCategories: boolean;
};

const initState = {
    Pattern: '',
    IgnoreCase: false,
    FromStart: false,

    results: [],
    showCategories: false,
    hasClientSearch: false,
    isSearching: false,
    isLoading: false,
    isScrolling: false,
    isNoResults: false,
    hasMore: false
};

const SEARCH_DEBOUNCE_TIME = 250;

const search_category_key = 'dd-search-category';

const getNextPreferredValue = (oldValue: priority.netitems.shared.FieldSelectType) => {
    if (oldValue === priority.netitems.shared.FieldSelectType.SearchName) {
        return priority.netitems.shared.FieldSelectType.SearchDes;
    } else if (oldValue === priority.netitems.shared.FieldSelectType.SearchDes) {
        return priority.netitems.shared.FieldSelectType.SearchEdes;
    }
    return priority.netitems.shared.FieldSelectType.SearchName;
};

const showCategorySearch = (
    isAllowed: boolean,
    searchType: priority.netitems.shared.FieldSelectType
) => {
    return (
        isAllowed &&
        (searchType === priority.netitems.shared.FieldSelectType.SearchEdes ||
            searchType === priority.netitems.shared.FieldSelectType.SearchDes ||
            searchType === priority.netitems.shared.FieldSelectType.SearchName)
    );
};

export const useSearch = <T = priority.netitems.form.ISelectValues>(
    ColumnID: number,
    Session: string,
    defaultSearchQuery: string = '',
    dataMapper?: (
        data: priority.netitems.form.ISelectValues,
        searchType: priority.netitems.shared.FieldSelectType
    ) => T,
    onCriticalError?: () => void,
    onRequestData?: any
) => {
    const [state, dispatchState] = useState<SearchState<T>>({
        ...initState,
        Pattern: defaultSearchQuery
    });

    const {searchType} = state;

    const setState = (partialState: Partial<SearchState<T>>) =>
        dispatchState(state => ({...state, ...partialState}));
    const clientDataRef = useRef<Array<T>>([]);
    const pageIndex = useRef(0);
    const isScrolling = useRef(false);

    const requestData = useCallback(
        async (params: {
            Pattern?: string;
            FromStart?: boolean;
            IgnoreCase?: boolean;
            Page?: number;
            Preferred?: priority.netitems.shared.FieldSelectType;
        }): Promise<priority.netitems.form.FieldSelectRs> => {
            const reqParams = {
                Session,
                Field: ColumnID,
                ColumnID: ColumnID,
                ...params
            };
            if (onRequestData) {
                return await onRequestData(reqParams);
            }
            return await fetchAdditionalChooses(reqParams, onCriticalError);
        },
        [Session, ColumnID, onCriticalError]
    );

    const onSearchAction = useCallback(promiseDebounce(requestData, SEARCH_DEBOUNCE_TIME), []);

    const parseSearchData = useCallback(
        (searchResponse: priority.netitems.form.FieldSelectRs) => {
            return {
                results: dataMapper
                    ? searchResponse.Values.map(item => dataMapper(item, searchType))
                    : (searchResponse.Values as T[]),
                hasMore: searchResponse.HaveMore,
                searchType: searchResponse.Type
            };
        },
        [dataMapper, searchType]
    );

    /**
     * OPEN
     **/

    const handleSearchOpen = useCallback(
        async (Pattern: string = '') => {
            setState({isLoading: true, Pattern});

            const searchTypeSysConst = await getSearchTypeSystemConst();
            const isCategorySearchAllowed = !!searchTypeSysConst;

            let Preferred: priority.netitems.shared.FieldSelectType;

            if (isCategorySearchAllowed) {
                const lastSelectedCategory = Number(localStorage.getItem(search_category_key));
                if (!Pattern && lastSelectedCategory) {
                    Preferred = lastSelectedCategory;
                } else {
                    Preferred = priority.netitems.shared.FieldSelectType.SearchName;
                }
            }

            const search = await requestData({
                Pattern,
                Page: 1,
                FromStart: state.FromStart,
                IgnoreCase: state.IgnoreCase,
                Preferred
            });

            const parsedData = parseSearchData(search);
            clientDataRef.current = parsedData.results;
            pageIndex.current = 1;

            const results =
                parsedData.searchType === priority.netitems.shared.FieldSelectType.Choose && Pattern
                    ? parsedData.results.filter(item =>
                          defaultSearchFilter(item, Pattern.toLowerCase())
                      )
                    : parsedData.results;

            const showCategories = showCategorySearch(
                isCategorySearchAllowed,
                parsedData.searchType
            );

            setState({
                isLoading: false,
                ...parsedData,
                results,
                showCategories,
                Preferred,
                hasClientSearch:
                    parsedData.searchType === priority.netitems.shared.FieldSelectType.Choose &&
                    parsedData.results.length >= 6
            });

            return search;
        },
        [state, requestData, dataMapper]
    );

    /**
     * SEARCH CHANGE
     **/

    const handleAsyncSearchChange = useCallback(
        async (Pattern: string) => {
            setState({isSearching: true, Pattern});

            const search: any = await onSearchAction({
                Pattern,
                Page: 1,
                FromStart: state.FromStart,
                IgnoreCase: state.IgnoreCase,
                Preferred: state.Preferred
            });

            setState({
                isSearching: false,
                ...parseSearchData(search)
            });
        },
        [state, onSearchAction]
    );

    /**
     * HANDLE CLIENT SEARCH
     **/

    const handleClientSearchChange = useCallback(async (Pattern: string) => {
        const results = clientDataRef.current.filter(item =>
            defaultSearchFilter(item, Pattern.toLowerCase())
        );
        setState({
            Pattern,
            results
        });
    }, []);

    /**
     * HANDLE SCROLL
     **/

    const handleScroll = useCallback(async () => {
        if (state.hasMore && !isScrolling.current) {
            isScrolling.current = true;

            const search = await requestData({
                Page: ++pageIndex.current,
                Pattern: state.Pattern,
                FromStart: state.FromStart,
                IgnoreCase: state.IgnoreCase,
                Preferred: state.Preferred
            });

            const parsedData = parseSearchData(search);
            const {results} = state;

            isScrolling.current = false;
            setState({
                ...parsedData,
                results: results.concat(parsedData.results)
            });
        }
    }, [state, requestData]);

    /**
     * SEARCH ACTION
     **/

    const __handleSearchAction = useCallback(
        async (params: Partial<SearchState<T>>) => {
            if (!state.isSearching) {
                setState({isSearching: true, ...params});

                const search: any = await onSearchAction({
                    FromStart: state.FromStart,
                    Pattern: state.Pattern,
                    IgnoreCase: state.IgnoreCase,
                    ...params
                });

                setState({
                    isSearching: false,
                    ...params,
                    ...parseSearchData(search)
                });
            }
        },
        [state, onSearchAction]
    );

    const handleIgnoreCaseChange = async () =>
        await __handleSearchAction({IgnoreCase: !state.IgnoreCase});

    const handleSearchCategoryChange = async () => {
        const nextSearchType = getNextPreferredValue(state.Preferred);
        localStorage.setItem(search_category_key, nextSearchType.toString());
        return await __handleSearchAction({Preferred: nextSearchType});
    };

    const handleSearchFromBeginningChange = useCallback(async () => {
        return await __handleSearchAction({FromStart: !state.FromStart});
    }, [state.FromStart, __handleSearchAction]);

    const handleSearchQueryChange = useCallback((Pattern: string = '') => {
        setState({Pattern});
    }, []);

    const handlers = {
        handleSearchOpen,
        handleAsyncSearchChange,
        handleScroll,
        handleIgnoreCaseChange,
        handleSearchFromBeginningChange,
        handleSearchQueryChange,
        handleClientSearchChange,
        handleSearchCategoryChange
    };

    return [state, handlers] as [SearchState<T>, SearchHandlers];
};

export interface SearchHandlers {
    handleSearchOpen(
        searchQuery?: string,
        isForceSearch?: boolean,
        callback?: () => void
    ): Promise<priority.netitems.form.FieldSelectRs>;

    handleAsyncSearchChange(searchQuery: string): Promise<void>;

    handleScroll(): Promise<void>;

    handleIgnoreCaseChange(): Promise<void>;

    handleSearchFromBeginningChange(): Promise<void>;

    handleSearchQueryChange(searchQuery: string): void;

    handleClientSearchChange(searchQuery: string): void;

    handleSearchCategoryChange(): Promise<void>;
}
